import { useEffect } from 'react'
import PropTypes from 'prop-types'

/**
 * The hook options.
 * @typedef {Object} useIntersectionObserverOpts
 * @property {object} intersectionObserverOpts - Indicates whether the Courage component is present.
 * @property {ScrollObserverCoreClasses} classes - Indicates whether the Power component is present.
 */

/**
 * Asynchronously observe intersections of a target reference element.
 * Please note that IE11 and other legacy browser do not implement the IntersectionObserver API
 * and therefore need to polyfill the missing functionality.
 * @param {React.Ref} ref – The reference to apply classes etc. on when the target matches.
 * @param {React.MutableRefObject<null|HTMLElement>|React.MutableRefObject<null|HTMLElement>[]} target – The reference to track the intersections on.
 * @param {useIntersectionObserverOpts} opts – Hook configuration.
 * @param {function} [onIntersectionHandler = () => {}] - The observer callback function.
 * @param {function} [onMatch= () => {}] - The callback function to invoke upon observed resizes.
 * @example
 *    const myTarget = useRef(null)
 *
 *    export function Example({}) {
 *      const myTarget = useRef(null);
 *      useIntersectionObserver({
 *        ref: sectionNavRef,
 *        target: myTarget,
 *        onIntersectionHandler: ScrollObserverCore.observeEntries,
 *        opts: {
 *          intersectionObserverOpts: { rootMargin: '0px 0px -100%' },
 *          classes: ScrollObserverCore.classes,
 *        },
 *      })
 *      return (
 *        <div ref={myTarget}>👋</div>
 *      );
 *    };
 */
function useIntersectionObserver({ ref, target, onIntersectionHandler = () => {}, opts, onMatch = () => {} } = {}) {
  useEffect(() => {
    const { current } = ref
    const currentTarget = Array.isArray(target) ? target.filter((t) => t?.current) : target?.current

    /**
     * If we have an {@link HTMLElement} to observe.
     */
    if (current && target) {
      const intersectionObserver = new IntersectionObserver((entries) => {
        onIntersectionHandler(current, opts, onMatch)(entries)
      }, opts?.intersectionObserverOpts)
      if (Array.isArray(target)) {
        currentTarget.filter((t) => t?.current).forEach((t) => intersectionObserver.observe(t?.current))
      } else {
        intersectionObserver.observe(target?.current)
      }
      return () => {
        if (Array.isArray(target)) {
          return currentTarget.filter((t) => t?.current).forEach((t) => intersectionObserver.unobserve(t?.current))
        }
        if (currentTarget) {
          return intersectionObserver.unobserve(currentTarget)
        }
        return () => {}
      }
    }
    return () => {}
  }, [onIntersectionHandler, ref, target, opts, onMatch])
}

useIntersectionObserver.propTypes = {
  // Use a function or the instance of a DOM native element.
  ref: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
  onIntersectionHandler: PropTypes.func,
  opts: PropTypes.oneOfType([PropTypes.object]),
}

export default useIntersectionObserver
